import React, { useMemo, useState } from "react";
import useSWR from "swr";
import { ChevronDown, ChevronUp, History } from "lucide-react";
import { api } from "@/lib/api";
import { OWNER_ROLE_LABELS, stageLabelMap } from "@/lib/stages";

/**
 * OPS-004B · Collapsible stage-history section for Operations project
 * detail. Consumes the ORDERED `stage_history` array from the OPS-004A
 * detail response — never fetches history separately, never re-sorts
 * client-side beyond the guaranteed canonical order.
 *
 * OPS-004B1 · Stage-key → label resolution reads the backend catalogue
 * (`/api/operations/stages/catalogue`) via SWR — no hardcoded map.
 */
export default function StageHistorySection({ history }) {
    const [open, setOpen] = useState(false);
    const rows = useMemo(() => (history || []), [history]);
    const count = rows.length;
    const { data: catalogue } = useSWR(
        "ops-stages-catalogue",
        () => api.operationsStageCatalogue(),
        { revalidateOnFocus: false, shouldRetryOnError: false },
    );
    const labels = useMemo(() => stageLabelMap(catalogue), [catalogue]);
    const stageLabel = (key) => (key ? (labels[key] || key) : null);

    return (
        <section
            data-testid="ops-project-stage-history"
            className="rounded-xl border border-[#E5E7EB] bg-white"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="ops-project-stage-history-toggle"
                aria-expanded={open}
                className="w-full flex items-center justify-between p-5 text-left"
            >
                <div className="flex items-center gap-2">
                    <History size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                    <span className="text-sm font-semibold text-[#131B2F]">Stage history</span>
                    <span
                        data-testid="ops-project-stage-history-count"
                        className="text-[11px] text-[#4B5565]"
                    >
                        {count} {count === 1 ? "transition" : "transitions"}
                    </span>
                </div>
                {open
                    ? <ChevronUp size={14} strokeWidth={1.75} className="text-[#131B2F]" />
                    : <ChevronDown size={14} strokeWidth={1.75} className="text-[#131B2F]" />}
            </button>

            {open && (
                count === 0 ? (
                    <div
                        data-testid="ops-project-stage-history-empty"
                        className="p-5 pt-0 text-[12px] text-[#4B5565]"
                    >
                        No transitions recorded yet — this project is still in its
                        opening stage. History grows only when a stage advances.
                    </div>
                ) : (
                    <ol
                        data-testid="ops-project-stage-history-list"
                        className="divide-y divide-[#F0F1F5] border-t border-[#F0F1F5]"
                    >
                        {rows.map((entry) => (
                            <li
                                key={entry.id || `${entry.from_stage || "start"}-${entry.entered_at}`}
                                data-testid="ops-project-stage-history-entry"
                                className="px-5 py-3 flex items-start justify-between gap-4"
                            >
                                <div className="min-w-0">
                                    <div className="text-[12px] font-medium text-[#131B2F]">
                                        {stageLabel(entry.from_stage) || "—"}
                                        <span className="mx-1 text-[#4B5565]">→</span>
                                        {stageLabel(entry.to_stage)}
                                    </div>
                                    {entry.actor && (
                                        <div className="mt-0.5 text-[11px] text-[#4B5565]">
                                            {entry.actor.name || entry.actor.id}
                                            {entry.actor.role
                                                ? ` · ${OWNER_ROLE_LABELS[entry.actor.role] || entry.actor.role}`
                                                : ""}
                                        </div>
                                    )}
                                    {entry.reason && (
                                        <div className="mt-0.5 text-[11px] text-[#4B5565] italic">
                                            {entry.reason}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[11px] text-[#4B5565] shrink-0 tabular-nums">
                                    {formatDateTime(entry.entered_at)}
                                </div>
                            </li>
                        ))}
                    </ol>
                )
            )}
        </section>
    );
}

function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            day: "numeric", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
}
